import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Loader from '../../components/atoms/Loader';
import SummaryFilterBar from '../../components/molecules/SummaryFilterBar';
import SummaryTable from '../../components/organisms/SummaryTable';
import SummaryColumnChart from '../../components/charts/SummaryColumnChart';
import { ALL_ROUTES, FILTER_TYPES } from '../../constants';
import {
  fetchAllPurposeOfUses,
  fetchAllRequestCount,
} from '../../services/DashboardService';
import { useSummaryFilters } from '../../hooks/useSummaryFilters';
import { exportSummaryToCSV } from '../../utils/csv';
import { isString, usePersistedState } from '../../utils/persistedState';
import { toSummaryGridRows } from '../../utils/summary';
import type {
  HealthSystemSummaryGridRow,
  HealthSystemSummaryRow,
  PurposeOfUseOption,
  SelectedDateRange,
} from '../../interfaces/Dashboard';

const DEFAULT_ROWS_PER_PAGE = 5;

/**
 * Landing page: aggregate request counts across all health systems, broken down
 * by the selected filter type, shown as a grid plus a single grouped column
 * chart.
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const filters = useSummaryFilters('summaryOverview');

  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<HealthSystemSummaryRow[]>([]);
  const [gridRows, setGridRows] = useState<HealthSystemSummaryGridRow[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange>(
    {
      fromdate: '',
      todate: '',
      filterType: FILTER_TYPES[0].label,
    },
  );

  const [purposeOfUses, setPurposeOfUses] = useState<PurposeOfUseOption[]>([]);
  const [selectedPurposeOfUse, setSelectedPurposeOfUseState] =
    useState<PurposeOfUseOption | null>(null);
  // Purpose-of-use options load asynchronously, so only the selected *value*
  // is persisted (validated once against the fetched list) rather than the
  // whole Option object - see useSummaryFilters for the synchronous case.
  const [persistedPurposeOfUse, setPersistedPurposeOfUse] = usePersistedState(
    'summaryOverview:purposeOfUse',
    '',
    isString,
  );
  const setSelectedPurposeOfUse = (value: PurposeOfUseOption | null) => {
    setSelectedPurposeOfUseState(value);
    setPersistedPurposeOfUse(value?.value ?? '');
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  /**
   * Loads the summary for the current filter selection. `overrides` lets the
   * Clear action fetch with default filters immediately rather than waiting for
   * the reset state to flush.
   */
  const loadSummary = useCallback(
    async (overrides?: {
      purposeOfUse?: PurposeOfUseOption | null;
      useDefaults?: boolean;
    }) => {
      setIsLoading(true);

      const {
        resolved,
        filterType,
        selectedDateRange: dateRange,
      } = filters.resolve(
        overrides?.useDefaults
          ? {
              filterType: FILTER_TYPES[0],
              dateFilter: { label: 'All', value: 'All' },
            }
          : undefined,
      );

      const purposeOfUse =
        overrides?.purposeOfUse !== undefined
          ? overrides.purposeOfUse
          : selectedPurposeOfUse;

      const response = await fetchAllRequestCount({
        fromdate: resolved.fromdate,
        todate: resolved.todate,
        filterType: filterType.value,
        isFilterByCreatedDate: overrides?.useDefaults
          ? false
          : filters.filterByRequestDate,
        purposeOfUse: purposeOfUse?.value,
      });

      setSummaryData(response.healthSystemwiseSummaryList);
      setGridRows(toSummaryGridRows(response.healthSystemwiseSummaryList));
      setSelectedDateRange(dateRange);
      setIsLoading(false);
    },
    [filters, selectedPurposeOfUse],
  );

  // Initial load: purpose-of-use options first, then the summary itself.
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const options = await fetchAllPurposeOfUses();
      if (cancelled) return;

      const defaultOption =
        options.find((option) => option.value === persistedPurposeOfUse) ??
        options[0] ??
        null;
      setPurposeOfUses(options);
      setSelectedPurposeOfUse(defaultOption);
      await loadSummary({ purposeOfUse: defaultOption });
    };

    void init();
    return () => {
      cancelled = true;
    };
    // Runs once on mount; loadSummary is intentionally not a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setPage(0);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    void loadSummary();
  };

  const handleClear = () => {
    const defaultPurposeOfUse = purposeOfUses[0] ?? null;
    filters.reset();
    setSelectedPurposeOfUse(defaultPurposeOfUse);
    setPage(0);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    void loadSummary({ purposeOfUse: defaultPurposeOfUse, useDefaults: true });
  };

  /**
   * The Facility breakdown is already per-health-system, so a purpose-of-use
   * filter is meaningful there; every other breakdown resets it to "All".
   */
  const handleFilterTypeChange = (value: typeof filters.filterType) => {
    filters.setFilterType(value);
    if (value.value !== 'FACILITY_WISE') {
      setSelectedPurposeOfUse(purposeOfUses[0] ?? null);
    }
  };

  return (
    <Container className='main-container'>
      <SummaryFilterBar
        filterType={filters.filterType}
        onFilterTypeChange={handleFilterTypeChange}
        dateFilter={filters.dateFilter}
        onDateFilterChange={filters.handleDateFilterChange}
        fromDate={filters.fromDate}
        toDate={filters.toDate}
        onDateChange={filters.handleDateChange}
        errors={filters.errors}
        filterByRequestDate={filters.filterByRequestDate}
        onFilterByRequestDateChange={filters.setFilterByRequestDate}
        onClear={handleClear}
        onApply={handleApply}
        isLoading={isLoading}
        purposeOfUses={purposeOfUses}
        selectedPurposeOfUse={selectedPurposeOfUse}
        onPurposeOfUseChange={setSelectedPurposeOfUse}
      />

      {isLoading ? (
        <Grid
          container
          className='all-resouces-list-wrapper request-container'
          spacing={3}
        >
          <Grid size={12}>
            <Paper className='respurce-list-paper' style={{ height: '500px' }}>
              <Loader fullHeight />
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          className='all-resouces-list-wrapper request-container'
          spacing={3}
        >
          <Grid size={12}>
            <Paper className='respurce-list-paper category-paper-wrapper'>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography
                    color='primary'
                    variant='h6'
                    className='category-name'
                  >
                    {selectedDateRange.filterType} Summary -{' '}
                    <span style={{ color: 'black' }}>
                      From {selectedDateRange.fromdate} To{' '}
                      {selectedDateRange.todate}
                    </span>
                  </Typography>
                </Grid>

                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'baseline',
                  }}
                >
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() =>
                      exportSummaryToCSV(gridRows, selectedDateRange.filterType)
                    }
                    className='show-req-btn'
                    title='Export to CSV'
                    disabled={gridRows.length === 0}
                  >
                    Export to CSV
                  </Button>

                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => navigate(ALL_ROUTES.DASHBOARD)}
                    className='show-req-btn'
                    title='Detail View'
                  >
                    Go to Detail View
                  </Button>
                </Grid>

                <Grid size={12}>
                  <SummaryTable
                    rows={gridRows}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={setPage}
                    onRowsPerPageChange={(value) => {
                      setRowsPerPage(value);
                      setPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid size={12}>
            <Paper className='respurce-list-paper'>
              <SummaryColumnChart
                data={summaryData}
                filterType={filters.filterType.value}
                height={500}
              />
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default LandingPage;
